import Phaser from 'phaser';
import { fetchCharacterDefinition } from '../ui/services/characterDefinitionService.ts';
import { assembleSprite } from './spriteAssembler.ts';
import { getCachedSprite, cacheSprite, SpriteCacheEntry } from './spriteCache.ts';
import { MultiatlasJSON, AnimsJSON } from './jsonGenerators.ts';

export const PLAYER_SPRITE_TEXTURE_KEY = 'player-character';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function teardownTexture(scene: Phaser.Scene, textureKey: string): void {
  // Remove stale animations in this texture's namespace first — they hold
  // references to Frame objects that become invalid when the texture is removed.
  const animPrefix = `${textureKey}:`;
  const staleKeys = ((scene.anims as any).anims.keys() as string[])
    .filter((k: string) => k.startsWith(animPrefix));
  for (const k of staleKeys) {
    scene.anims.remove(k);
  }
  if (scene.textures.exists(textureKey)) {
    scene.textures.remove(textureKey);
  }
}

function bitmapToCanvas(bitmap: ImageBitmap): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0);
  bitmap.close();
  return canvas;
}

async function blobToCanvas(blob: Blob): Promise<HTMLCanvasElement> {
  const bitmap = await createImageBitmap(blob);
  return bitmapToCanvas(bitmap);
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob returned null'))), 'image/png')
  );
}

function injectAtlas(
  scene: Phaser.Scene,
  textureKey: string,
  canvases: HTMLCanvasElement[],
  atlasJSON: MultiatlasJSON,
): void {
  // addAtlasJSONArray expects the textures array from the multiatlas JSON, not the
  // root JSON object. This matches how SpriteZipLoader injects its textures.
  (scene.textures as any).addAtlasJSONArray(textureKey, canvases, atlasJSON.textures);
}

function injectAnims(scene: Phaser.Scene, textureKey: string, animsJSON: AnimsJSON): void {
  // Patch frame key references and namespace anim keys, replicating what
  // SpriteZipLoader's remapAnimationKeys + namespaceAnimationKeys do.
  const patchedAnims = animsJSON.anims.map((anim) => ({
    ...anim,
    key: `${textureKey}:${anim.key}`,
    frames: anim.frames.map((f) => ({ ...f, key: textureKey })),
  }));
  scene.anims.fromJSON({
    anims: patchedAnims as unknown as Phaser.Types.Animations.JSONAnimation[],
    globalTimeScale: 1,
  });
}

async function storeInCache(
  spriteHash: string,
  canvases: HTMLCanvasElement[],
  atlasJSON: MultiatlasJSON,
  animsJSON: AnimsJSON,
): Promise<void> {
  try {
    const pages = await Promise.all(canvases.map(canvasToBlob));
    await cacheSprite({ hash: spriteHash, pages, atlasJSON, animsJSON });
  } catch (err) {
    // Cache failure is non-fatal — the sprite is already injected into Phaser.
    console.warn('[PhaserSpriteLoader] Failed to cache sprite:', err);
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Load a character sprite into a Phaser scene by spriteHash.
 *
 * Checks IndexedDB first; on a cache miss, fetches the CharacterDefinition,
 * assembles the sprite atlas locally, caches the result, and injects it into
 * Phaser — bypassing the server ZIP pipeline entirely.
 *
 * Any existing texture + animations registered under textureKey are torn down
 * before the new ones are injected, so this is safe to call for both initial
 * loads and hot-reloads.
 */
export async function loadCharacterSprite(
  scene: Phaser.Scene,
  textureKey: string,
  spriteHash: string,
  characterId: string,
): Promise<void> {
  teardownTexture(scene, textureKey);

  const cached = await getCachedSprite(spriteHash);

  if (cached) {
    console.log(`[PhaserSpriteLoader] Cache hit for ${spriteHash.slice(0, 8)}…`);
    const canvases = await Promise.all(cached.pages.map(blobToCanvas));
    injectAtlas(scene, textureKey, canvases, cached.atlasJSON);
    injectAnims(scene, textureKey, cached.animsJSON);
    return;
  }

  console.log(`[PhaserSpriteLoader] Cache miss for ${spriteHash.slice(0, 8)}… assembling.`);
  const definition = await fetchCharacterDefinition(characterId);
  const assembled = await assembleSprite(definition);

  // Convert ImageBitmaps to HTMLCanvasElements (Phaser requires DOM canvas elements).
  const canvases = assembled.pages.map(bitmapToCanvas);

  injectAtlas(scene, textureKey, canvases, assembled.atlasJSON);
  injectAnims(scene, textureKey, assembled.animsJSON);

  // Cache asynchronously — don't block the caller.
  storeInCache(spriteHash, canvases, assembled.atlasJSON, assembled.animsJSON);
}

// Re-export for callers that want to reference this without importing sprites.ts
export type { SpriteCacheEntry };
